import React from 'react';
import { FormStateBasic } from 'informed';
import { useTranslation } from 'react-i18next';
import Yup from 'yup';

import {
  ButtonSize,
  buttonSize,
  ButtonVariant,
  buttonVariant,
  InformedInternationalPhoneNumberField
} from '@nw/design-system/omega';
import { allowedCountryCodeEnums } from '@nw/shared/config';

import { userAuthHandlerMachineEvents } from '../../constants/userAuthHandlerMachineConstants';
import {
  DoChildMachineSelfTransition,
  OnClickGetOtpButton,
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType
} from '../../types/UserAuthHandlerMachineTypes';

import { AuthFormContainerButton } from '../AuthFormSection/styledComponents';

import {
  AuthFormContainerHeading,
  AuthPhoneNumberInputCardContainer,
  ErrorText,
  GetOTPText,
  PhoneNumberCardCustomDescription,
  PhoneNumberCardCustomHeading
} from './styledComponents';

interface AuthPhoneNumberInputCardProps {
  errors: FormStateBasic['errors'];
  touched: FormStateBasic['touched'];
  values: FormStateBasic['values'];
  validationSchema: Yup.AnyObjectSchema;
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
}

const AuthPhoneNumberInputCard: React.FC<AuthPhoneNumberInputCardProps> = ({
  errors,
  touched,
  values,
  validationSchema,
  userAuthHandlerMachineSend,
  userAuthHandlerMachineState
}) => {
  const field = 'phoneNumber';
  const { t } = useTranslation();

  const {
    errorMessage,
    isAuthButtonLoading,
    shouldDoChildMachineSelfTransition,
    clientConfigDetails
  } = userAuthHandlerMachineState.context;

  const isGetOtpButtonDisabled = () => {
    if (
      errors[field] ||
      !values[field] ||
      values[field].toString().length < 10
    ) {
      return true;
    }
    return false;
  };

  const onClickGetOtpButton = () => {
    if (shouldDoChildMachineSelfTransition) {
      userAuthHandlerMachineSend({
        type: userAuthHandlerMachineEvents.doChildMachineSelfTransition
      } as DoChildMachineSelfTransition);
    } else
      userAuthHandlerMachineSend({
        type: userAuthHandlerMachineEvents.onClickGetOtpButton,
        values: {
          phoneNumber: values[field]
        }
      } as OnClickGetOtpButton);
  };

  const renderGetOtpButton = () => {
    return (
      <AuthFormContainerButton
        id={'getOTPButton'}
        isLoading={isAuthButtonLoading}
        variant={buttonVariant.primary as ButtonVariant}
        size={buttonSize.large as ButtonSize}
        type={'submit'}
        className={'getOTPButtonStyles'}
        isDisabled={isGetOtpButtonDisabled()}
        onClick={onClickGetOtpButton}
      >
        <GetOTPText>{t('common.getOTP')}</GetOTPText>
      </AuthFormContainerButton>
    );
  };

  const renderPhoneNumberField = () => {
    const fieldValidations = {
      validateOnBlur: true,
      validateOnChange: true
    };
    return (
      <InformedInternationalPhoneNumberField
        label={t('common.mobileNumber')}
        placeholder={'Enter Number'}
        field={field}
        values={values}
        touched={touched[field]}
        error={errors[field]}
        validationSchemaObject={validationSchema}
        isDisabled={isAuthButtonLoading}
        autoFocus={true}
        popOverStyle={'secondary'}
        t={t}
        fieldValidations={fieldValidations}
        allowedCountriesList={allowedCountryCodeEnums}
        wrapperWithPopOverStyles={'phoneNumberFieldPopOverStyles'}
        intlPhoneNumberFieldStyles={'phoneNumberContainerStyles'}
        labelStyles={'phoneNumberFieldLabelStyles'}
        phoneNumberInputStyles={'phoneNumberFieldInputStyles'}
      />
    );
  };

  const renderAuthFormContainerHeading = () => {
    if (clientConfigDetails?.phoneNumberCardConfig) {
      return (
        <>
          <PhoneNumberCardCustomHeading
            className={'phone-number-card-custom-heading'}
          >
            {clientConfigDetails.phoneNumberCardConfig.heading}
          </PhoneNumberCardCustomHeading>
          {clientConfigDetails.phoneNumberCardConfig?.description && (
            <PhoneNumberCardCustomDescription
              className={'phone-number-card-custom-description'}
            >
              {clientConfigDetails.phoneNumberCardConfig?.description}
            </PhoneNumberCardCustomDescription>
          )}
        </>
      );
    }

    return (
      <AuthFormContainerHeading
        data-testid={'authPhoneNumberInputCardHeading'}
        className={'authPhoneNumberInputCardHeading'}
      >
        {t('common.login_signup')}
      </AuthFormContainerHeading>
    );
  };

  const renderErrorMessage = () => {
    return (
      <ErrorText className="login-error-message-text">{errorMessage}</ErrorText>
    );
  };

  return (
    <AuthPhoneNumberInputCardContainer
      className={'auth-phone-number-input-card-container'}
    >
      {renderAuthFormContainerHeading()}
      {renderPhoneNumberField()}
      {renderGetOtpButton()}
      {renderErrorMessage()}
    </AuthPhoneNumberInputCardContainer>
  );
};

export default AuthPhoneNumberInputCard;
